import { inject, Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, Router } from '@angular/router';
import { FeatureFlagsService } from '@services/feature-flags.service';
import { getFullRoutePath } from '@services/guards/get-full-route-path';
import { Guard } from '@services/guards/guard';
import { routePatternToRegExp } from '@services/guards/route-pattern-to-reg-exp';
import { RoutesFeatureFlagsMapService } from '@services/guards/routes-feature-flags-map.service';
import { UserService } from '@services/user.service';
import { FeatureFlagContextAttributes } from '@shared/models/feature-flag-context-attributes';

@Injectable({ providedIn: 'root' })
export class AuthGuard implements Guard {
  protected readonly router = inject(Router);
  protected readonly userService = inject(UserService);
  protected readonly featureFlagsService = inject(FeatureFlagsService);
  protected readonly routesFeatureFlagsMapService = inject(RoutesFeatureFlagsMapService);

  async canActivate(route: ActivatedRouteSnapshot) {
    if (!this.userService.isUserAuthenticated()) return false;

    const routeUrl = getFullRoutePath(route);
    const featureFlagKey = await this.routesFeatureFlagsMapService.getFeatureFlagKey(routeUrl);
    return this.isAccessGranted(routeUrl, featureFlagKey, route.params, true);
  }

  async canShow(routeUrl: string, params: Record<string, string>) {
    if (!this.userService.isUserAuthenticated()) return false;

    const featureFlagKey = await this.routesFeatureFlagsMapService.getFeatureFlagKey(routeUrl);
    return this.isAccessGranted(routeUrl, featureFlagKey, params);
  }

  async isAccessGranted(routeUrl: string, featureFlagKey: string, params: Record<string, string>, log = false) {
    if (!this.userService.isUserAuthenticated()) return false;

    const allowedRoutes = UserService.getCurrentRole().allowedRoutes ?? [];
    let isAccessGranted = allowedRoutes.some(routePattern => routePatternToRegExp(routePattern).test(routeUrl));
    if (!isAccessGranted && log) console.warn('AuthGuard', routeUrl);

    if (isAccessGranted && featureFlagKey.length && this.featureFlagExists(featureFlagKey)) {
      isAccessGranted = await this.isFeatureFlagOn(featureFlagKey, this.getContext(params));
      if (!isAccessGranted && log) console.warn('AuthGuard', featureFlagKey);
    }

    return isAccessGranted;
  }

  protected featureFlagExists(featureFlagKey: string): boolean {
    featureFlagKey = featureFlagKey.replace(/\//gi, '|');
    return this.featureFlagsService.exists(featureFlagKey);
  }

  protected async isFeatureFlagOn(featureFlagKey: string, context: FeatureFlagContextAttributes): Promise<boolean> {
    featureFlagKey = featureFlagKey.replace(/\//gi, '|');
    return await this.featureFlagsService.isOnWithContext(featureFlagKey, context);
  }

  protected getContext(params: Record<string, string>): FeatureFlagContextAttributes {
    const context: FeatureFlagContextAttributes = {
      ncpdps: [],
      programGroups: [],
      regions: [],
      tenants: [],
    };

    if (params.ncpdp) context.ncpdps?.push(params.ncpdp);
    if (params.programGroup) context.programGroups?.push(params.programGroup);
    if (params.region) context.regions?.push(params.region);
    if (params.tenantId) context.tenants?.push(params.tenantId);

    return context;
  }
}
